import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './context/AuthContext';
import { useFeatureFlags } from './context/FeatureFlagContext';
import ErrorBoundary from './components/ErrorBoundary';
import NavBar from './components/NavBar/NavBar';
import ScrollToTop from './components/ScrollToTop';
import VisitTracker from './components/VisitTracker';
import SmartAppBanner from './components/AppPromo/SmartAppBanner';
import './App.css';

/* ── Lazy-loaded routes ── */

// Public
const Home = lazy(() => import('./components/Home'));
const Login = lazy(() => import('./components/Authentication/Login'));
const DriverLoginForm = lazy(() => import('./components/Driver/DriverLoginForm'));
const DriverRegistrationForm = lazy(() => import('./components/Driver/DriverRegistrationForm'));
const About = lazy(() => import('./pages/About'));
const Careers = lazy(() => import('./pages/Careers'));
const Blog = lazy(() => import('./pages/Blog'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const Security = lazy(() => import('./pages/Security'));
const Accessibility = lazy(() => import('./pages/Accessibility'));
const Corporate = lazy(() => import('./pages/Corporate'));
const CorporateConciergePortal = lazy(() => import('./pages/CorporateConciergePortal'));
const DeleteAccount = lazy(() => import('./components/DeleteAccount'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const TrackRide = lazy(() => import('./pages/TrackRide'));
const RideReceipt = lazy(() => import('./pages/RideReceipt'));
const FreezeTocxiId = lazy(() => import('./pages/FreezeTocxiId'));
const NotFound = lazy(() => import('./components/NotFound'));
const RoutePage = lazy(() => import('./pages/RoutePage'));
const CabRoutesIndex = lazy(() => import('./pages/CabRoutesIndex'));
const PromoterRedirect = lazy(() => import('./components/PromoterRedirect'));

// Admin
const AdminLogin = lazy(() => import('./components/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));

// Authenticated user
const Bookings = lazy(() => import('./components/Booking/Bookings'));
const OndcTicketHistory = lazy(() => import('./components/Booking/OndcTicketHistory'));
const FeaturesHub = lazy(() => import('./components/Features/FeaturesHub'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const ProfileSettings = lazy(() => import('./components/Profile/ProfileSettings'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const DashboardSupport = lazy(() => import('./components/Dashboard/Support'));
const Promotions = lazy(() => import('./components/Dashboard/Promotions'));
const BookingConfirmation = lazy(() => import('./components/Booking/BookingConfirmation'));
const Wallet = lazy(() => import('./components/Wallet/Wallet'));
const Parcels = lazy(() => import('./components/Parcel/Parcels'));
const SafetyCenter = lazy(() => import('./components/Safety/SafetyCenter'));
const ParkingHub = lazy(() => import('./components/Parking/ParkingHub'));
const Subscriptions = lazy(() => import('./components/Subscription/Subscriptions'));
const Rewards = lazy(() => import('./components/Rewards/Rewards'));
const BusTracking = lazy(() => import('./components/Bus/BusTracking'));
const CommuteBuddy = lazy(() => import('./components/Commute/CommuteBuddy'));
const FuelStationHub = lazy(() => import('./components/Fuel/FuelStationHub'));
const OutstationBooking = lazy(() => import('./components/Outstation/OutstationBooking'));
const TourPackages = lazy(() => import('./components/Tours/TourPackages'));
const FleetOperatorDashboard = lazy(() => import('./components/FleetOperator/FleetOperatorDashboard'));
const OperatorOnboarding = lazy(() => import('./components/FleetOperator/OperatorOnboarding'));

// Driver
const DriverManagement = lazy(() => import('./components/Driver/DriverManagement'));
const DriverLeaderboard = lazy(() => import('./components/Driver/DriverLeaderboard'));

/* ── Loading fallback ── */
const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh', color: '#6B7280', fontSize: '0.95rem',
  }}>
    Loading...
  </div>
);

/* ── NavBar wrapper (hidden on admin routes) ── */
const NavBarWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <NavBar />;
};

/* ── Skip-to-content link for accessibility ── */
const SkipNav = () => (
  <a
    href="#main-content"
    style={{
      position: 'absolute', left: '-9999px', top: 'auto',
      width: '1px', height: '1px', overflow: 'hidden',
      zIndex: 9999, padding: '0.75rem 1.5rem',
      background: '#0a74da', color: '#fff', fontWeight: 600,
      textDecoration: 'none', borderRadius: '0 0 8px 0',
    }}
    onFocus={(e) => { e.target.style.left = '0'; e.target.style.width = 'auto'; e.target.style.height = 'auto'; }}
    onBlur={(e) => { e.target.style.left = '-9999px'; e.target.style.width = '1px'; e.target.style.height = '1px'; }}
  >
    Skip to main content
  </a>
);

function App() {
  const { isAuthenticated, userRole } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={4000} />
      <ScrollToTop />
      <VisitTracker />
      <SkipNav />
      <div>
        <NavBarWrapper />
        <SmartAppBanner />
        <main id="main-content">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/DriverLoginForm" element={<DriverLoginForm />} />
                <Route path="/DriverRegistrationForm" element={<DriverRegistrationForm />} />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/support" element={<Support />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/security" element={<Security />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/corporate" element={<Corporate />} />
                <Route path="/corporate/concierge" element={<CorporateConciergePortal />} />
                <Route path="/corporate/concierge/:corpId" element={<CorporateConciergePortal />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/invite/:referralCode" element={<InvitePage />} />
                <Route path="/r/:code" element={<PromoterRedirect />} />
                <Route path="/track/:rideToken" element={<TrackRide />} />
                <Route path="/receipt/:rideToken" element={<RideReceipt />} />
                <Route path="/freeze" element={<FreezeTocxiId />} />
                {/* SEO Route Pages */}
                <Route path="/cab" element={<CabRoutesIndex />} />
                <Route path="/cab/:routeSlug" element={<RoutePage />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                {isAuthenticated && userRole === 'ADMIN' && (
                  <Route path="/admin" element={<AdminDashboard />} />
                )}
                {!isAuthenticated && (
                  <Route path="/admin" element={<AdminLogin />} />
                )}

                {/* Authenticated user routes */}
                {isAuthenticated && userRole === 'USER' && (
                  <>
                    <Route path="/bookings" element={<Bookings />} />
                    <Route path="/my-tickets" element={<OndcTicketHistory />} />
                    <Route path="/features" element={<FeaturesHub />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/settings" element={<ProfileSettings />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/dashboard-support" element={<DashboardSupport />} />
                    <Route path="/promotions" element={<Promotions />} />
                    <Route path="/booking-confirmation" element={<BookingConfirmation />} />
                    <Route path="/wallet" element={<Wallet />} />
                    <Route path="/parcels" element={<Parcels />} />
                    <Route path="/safety" element={<SafetyCenter />} />
                    {isFeatureEnabled('parking') && <Route path="/parking" element={<ParkingHub />} />}
                    <Route path="/subscriptions" element={<Subscriptions />} />
                    <Route path="/rewards" element={<Rewards />} />
                    <Route path="/bus" element={<BusTracking />} />
                    {isFeatureEnabled('commuteBuddy') && <Route path="/commute" element={<CommuteBuddy />} />}
                    <Route path="/energy" element={<FuelStationHub />} />
                    {isFeatureEnabled('outstation') && <Route path="/outstation" element={<OutstationBooking />} />}
                    {isFeatureEnabled('tours') && <Route path="/tours" element={<TourPackages />} />}
                    <Route path="/fleet" element={<FleetOperatorDashboard />} />
                    <Route path="/operator-onboarding" element={<OperatorOnboarding />} />
                  </>
                )}

                {/* Authenticated driver routes */}
                {isAuthenticated && userRole === 'DRIVER' && (
                  <>
                    <Route path="/drivers" element={<DriverManagement />} />
                    <Route path="/drivers/leaderboard" element={<DriverLeaderboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<ProfileSettings />} />
                    <Route path="/safety" element={<SafetyCenter />} />
                    <Route path="/wallet" element={<Wallet />} />
                    <Route path="/dashboard-support" element={<DashboardSupport />} />
                    <Route path="/promotions" element={<Promotions />} />
                  </>
                )}

                {/* 404 catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </Router>
  );
}

export default App;
